export const AUTO_BIDDING_STRATEGIES = [
  {
    id: 'profit_focused',
    label: 'Profit Focused',
    description: 'Adjust the bids to reach campaign profitability.',
  },
  {
    id: 'acos_focused',
    label: 'ACOS Focused',
    description: 'Adjust the bids to reduce the ACOS.',
  },
  {
    id: 'awareness',
    label: 'Awareness',
    description: 'Adjust the bids to increase the impressions and CTR.',
  },
] as const;

export type AutoBiddingStrategy = (typeof AUTO_BIDDING_STRATEGIES)[number]['id'];

export const AUTO_BIDDING_FREQUENCIES = [
  { id: 'hourly', label: 'Hourly' },
  { id: 'every_6h', label: 'Every 6 hours' },
  { id: 'daily', label: 'Daily' },
  { id: 'weekly', label: 'Weekly' },
] as const;

export type AutoBiddingFrequency = (typeof AUTO_BIDDING_FREQUENCIES)[number]['id'];

export type AutoBiddingSettings = {
  enabled: boolean;
  strategy: AutoBiddingStrategy | null;
  targetAcos: number | null;
  biddingFrequency: AutoBiddingFrequency | null;
  maxBid: number | null;
  autoAddTargets: boolean;
  reduceOutOfBudget: boolean;
  lastRunAt: string | null;
  nextRunAt: string | null;
  campaignId?: string;
  adProduct?: string;
};

export type AutoBiddingSavePayload = {
  strategy: AutoBiddingStrategy;
  targetAcos: number;
  biddingFrequency: AutoBiddingFrequency;
  maxBid: number | null;
  autoAddTargets: boolean;
  reduceOutOfBudget: boolean;
};

export type AutoBiddingPreviewChange = {
  kind: 'keyword' | 'target';
  id: string;
  text: string | null;
  adGroupId: string | null;
  previousBid: number;
  proposedBid: number;
  reason: string;
  direction: string;
};

export type AutoBiddingPreviewHarvest = {
  kind?: 'harvest';
  text: string;
  adGroupId: string;
  proposedBid: number;
  reason: string;
};

export type AutoBiddingPreview = {
  enabled: boolean;
  strategy: AutoBiddingStrategy | null;
  targetAcos: number | null;
  nextRunAt: string | null;
  campaignAcos: number | null;
  outOfBudget: boolean;
  changes: AutoBiddingPreviewChange[];
  harvests: AutoBiddingPreviewHarvest[];
  error: string | null;
};

export type AutoBiddingRunLogItem = {
  kind: string;
  entityId: string | null;
  entityText: string | null;
  adGroupId: string | null;
  previousBid: number | null;
  newBid: number | null;
  reason: string | null;
  direction: string | null;
  status: string;
};

export type AutoBiddingRunLog = {
  id: string;
  runAt: string | null;
  status: string;
  message: string | null;
  bidUpdates: number;
  bidSkips: number;
  harvests: number;
  items: AutoBiddingRunLogItem[];
};

export type AutoBiddingHistoryRow = {
  id: string;
  runAt: string | null;
  entity: string;
  type: string;
  previousBid: number | null;
  newBid: number | null;
  reason: string;
  status: string;
};

export function apiErrorMessage(error: unknown, fallback: string): string {
  if (error && typeof error === 'object' && 'response' in error) {
    const detail = (error as { response?: { data?: { detail?: unknown } } }).response?.data?.detail;
    if (typeof detail === 'string' && detail.trim()) return detail;
  }
  return error instanceof Error ? error.message : fallback;
}

export function supportsAutoBidding(adProduct: string): boolean {
  return adProduct === 'SP' || adProduct === 'SB';
}

export function autoBiddingStrategyLabel(strategy: AutoBiddingStrategy | null | undefined): string | null {
  if (!strategy) return null;
  return AUTO_BIDDING_STRATEGIES.find((item) => item.id === strategy)?.label ?? null;
}

export function autoBiddingFrequencyLabel(
  frequency: AutoBiddingFrequency | null | undefined
): string | null {
  if (!frequency) return null;
  return AUTO_BIDDING_FREQUENCIES.find((item) => item.id === frequency)?.label ?? null;
}

export function formatAutoBiddingAcos(value: number | null | undefined): string | null {
  if (value == null || !Number.isFinite(value)) return null;
  return `${value.toFixed(value % 1 === 0 ? 0 : 1)}%`;
}

/** Compact label shown beside an enabled auto-bidding switch. */
export function formatAutoBiddingSummary(settings: AutoBiddingSettings | null | undefined): string {
  if (!settings?.enabled) return '';
  const parts: string[] = [];
  const strategy = autoBiddingStrategyLabel(settings.strategy);
  if (strategy) parts.push(strategy);
  const target = formatAutoBiddingAcos(settings.targetAcos);
  if (target) parts.push(`Target ACOS ${target}`);
  const frequency = autoBiddingFrequencyLabel(settings.biddingFrequency);
  if (frequency) parts.push(frequency);
  if (settings.autoAddTargets) parts.push('Add targets');
  if (settings.reduceOutOfBudget) parts.push('Reduce out of budget');
  return parts.join(' · ');
}

export function parseAutoBiddingSettings(raw: unknown): AutoBiddingSettings {
  const data = raw && typeof raw === 'object' ? (raw as Record<string, unknown>) : {};
  const strategy = AUTO_BIDDING_STRATEGIES.some((s) => s.id === data.strategy)
    ? (data.strategy as AutoBiddingStrategy)
    : null;
  const biddingFrequency = AUTO_BIDDING_FREQUENCIES.some((f) => f.id === data.biddingFrequency)
    ? (data.biddingFrequency as AutoBiddingFrequency)
    : null;
  return {
    enabled: data.enabled === true,
    strategy,
    targetAcos: typeof data.targetAcos === 'number' ? data.targetAcos : null,
    biddingFrequency,
    maxBid: typeof data.maxBid === 'number' ? data.maxBid : null,
    autoAddTargets: data.autoAddTargets === true,
    reduceOutOfBudget: data.reduceOutOfBudget === true,
    lastRunAt: typeof data.lastRunAt === 'string' ? data.lastRunAt : null,
    nextRunAt: typeof data.nextRunAt === 'string' ? data.nextRunAt : null,
    campaignId: typeof data.campaignId === 'string' ? data.campaignId : undefined,
    adProduct: typeof data.adProduct === 'string' ? data.adProduct : undefined,
  };
}

export function validateAutoBiddingForm(input: {
  strategy: AutoBiddingStrategy | null;
  targetAcos: string;
  biddingFrequency: AutoBiddingFrequency | '';
  maxBid: string;
}): string | null {
  if (!input.strategy) return 'Select a bidding strategy';
  const acos = Number(input.targetAcos);
  if (!input.targetAcos.trim() || !Number.isFinite(acos) || acos <= 0) {
    return 'Target ACOS is required and must be greater than 0';
  }
  if (!input.biddingFrequency) return 'Bidding frequency is required';
  if (input.maxBid.trim()) {
    const maxBid = Number(input.maxBid);
    if (!Number.isFinite(maxBid) || maxBid <= 0) {
      return 'Max bid must be greater than 0 when set';
    }
  }
  return null;
}

export function buildAutoBiddingSavePayload(input: {
  strategy: AutoBiddingStrategy;
  targetAcos: string;
  biddingFrequency: AutoBiddingFrequency;
  maxBid: string;
  autoAddTargets: boolean;
  reduceOutOfBudget: boolean;
}): AutoBiddingSavePayload {
  return {
    strategy: input.strategy,
    targetAcos: Number(input.targetAcos),
    biddingFrequency: input.biddingFrequency,
    maxBid: input.maxBid.trim() ? Number(input.maxBid) : null,
    autoAddTargets: input.autoAddTargets,
    reduceOutOfBudget: input.reduceOutOfBudget,
  };
}

function asRecord(raw: unknown): Record<string, unknown> {
  return raw && typeof raw === 'object' ? (raw as Record<string, unknown>) : {};
}

function asString(value: unknown): string | null {
  return typeof value === 'string' && value.trim() ? value : null;
}

function asNumber(value: unknown): number | null {
  if (typeof value === 'number' && Number.isFinite(value)) return value;
  if (typeof value === 'string' && value.trim()) {
    const parsed = Number(value);
    if (Number.isFinite(parsed)) return parsed;
  }
  return null;
}

function parsePreviewChange(raw: unknown): AutoBiddingPreviewChange | null {
  const data = asRecord(raw);
  const kind = data.kind === 'target' ? 'target' : data.kind === 'keyword' ? 'keyword' : null;
  const id = asString(data.id);
  const previousBid = asNumber(data.previousBid);
  const proposedBid = asNumber(data.proposedBid);
  if (!kind || !id || previousBid == null || proposedBid == null) return null;
  return {
    kind,
    id,
    text: asString(data.text),
    adGroupId: asString(data.adGroupId),
    previousBid,
    proposedBid,
    reason: asString(data.reason) ?? '',
    direction: asString(data.direction) ?? '',
  };
}

function parsePreviewHarvest(raw: unknown): AutoBiddingPreviewHarvest | null {
  const data = asRecord(raw);
  const text = asString(data.text);
  const adGroupId = asString(data.adGroupId);
  const proposedBid = asNumber(data.proposedBid);
  if (!text || !adGroupId || proposedBid == null) return null;
  return {
    kind: 'harvest',
    text,
    adGroupId,
    proposedBid,
    reason: asString(data.reason) ?? 'Converting search term',
  };
}

export function emptyAutoBiddingPreview(): AutoBiddingPreview {
  return {
    enabled: false,
    strategy: null,
    targetAcos: null,
    nextRunAt: null,
    campaignAcos: null,
    outOfBudget: false,
    changes: [],
    harvests: [],
    error: null,
  };
}

export function parseAutoBiddingPreview(raw: unknown): AutoBiddingPreview {
  const data = asRecord(raw);
  const strategy = AUTO_BIDDING_STRATEGIES.some((s) => s.id === data.strategy)
    ? (data.strategy as AutoBiddingStrategy)
    : null;
  return {
    enabled: data.enabled === true,
    strategy,
    targetAcos: asNumber(data.targetAcos),
    nextRunAt: asString(data.nextRunAt),
    campaignAcos: asNumber(data.campaignAcos),
    outOfBudget: data.outOfBudget === true,
    changes: Array.isArray(data.changes)
      ? data.changes.map(parsePreviewChange).filter((row): row is AutoBiddingPreviewChange => row != null)
      : [],
    harvests: Array.isArray(data.harvests)
      ? data.harvests.map(parsePreviewHarvest).filter((row): row is AutoBiddingPreviewHarvest => row != null)
      : [],
    error: asString(data.error),
  };
}

function parseRunLogItem(raw: unknown): AutoBiddingRunLogItem {
  const data = asRecord(raw);
  return {
    kind: asString(data.kind) ?? '',
    entityId: asString(data.entityId),
    entityText: asString(data.entityText),
    adGroupId: asString(data.adGroupId),
    previousBid: asNumber(data.previousBid),
    newBid: asNumber(data.newBid),
    reason: asString(data.reason),
    direction: asString(data.direction),
    status: asString(data.status) ?? '',
  };
}

export function parseAutoBiddingRunLogs(raw: unknown): AutoBiddingRunLog[] {
  if (!Array.isArray(raw)) return [];
  return raw.map((row, index) => {
    const data = asRecord(row);
    return {
      id: asString(data.id) ?? `run-${index}`,
      runAt: asString(data.runAt),
      status: asString(data.status) ?? '',
      message: asString(data.message),
      bidUpdates: asNumber(data.bidUpdates) ?? 0,
      bidSkips: asNumber(data.bidSkips) ?? 0,
      harvests: asNumber(data.harvests) ?? 0,
      items: Array.isArray(data.items) ? data.items.map(parseRunLogItem) : [],
    };
  });
}

export function autoBiddingEntityLabel(kind: string): string {
  if (kind === 'target') return 'Target';
  if (kind === 'harvest') return 'Harvest';
  return 'Keyword';
}

export function flattenAutoBiddingHistory(logs: AutoBiddingRunLog[]): AutoBiddingHistoryRow[] {
  const rows: AutoBiddingHistoryRow[] = [];
  for (const log of logs) {
    log.items.forEach((item, index) => {
      rows.push({
        id: `${log.id}-${index}`,
        runAt: log.runAt,
        entity: item.entityText || item.entityId || '—',
        type: autoBiddingEntityLabel(item.kind),
        previousBid: item.previousBid,
        newBid: item.newBid,
        reason: item.reason ?? '',
        status: item.status || log.status,
      });
    });
  }
  return rows;
}

export function autoBiddingHistorySearchValues(row: AutoBiddingHistoryRow): unknown[] {
  return [row.runAt, row.entity, row.type, row.previousBid, row.newBid, row.reason, row.status];
}
